import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Star } from "lucide-react";
import { toast } from "sonner";
import { api, img, errMsg, useSEO } from "../../lib/api";
import { useShop } from "../../context/ShopContext";
import ProductCard from "../../components/store/ProductCard";

function Section({ eyebrow, title, link, items, testid }) {
  if (!items?.length) return null;
  return (
    <section className="container-x mt-20 sm:mt-28" data-testid={testid}>
      <div className="flex items-end justify-between gap-4 mb-8">
        <div><p className="eyebrow mb-2">{eyebrow}</p><h2 className="text-3xl sm:text-4xl text-[#1B3B2B]">{title}</h2></div>
        {link && <Link to={link} className="text-sm text-[#1B3B2B] flex items-center gap-1 hover:gap-2 transition-[gap] whitespace-nowrap">View all <ArrowRight className="w-4 h-4" /></Link>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">{items.map((p) => <ProductCard key={p.id} p={p} />)}</div>
    </section>
  );
}

function Hero({ hero, banners }) {
  const b = banners[0];
  const h = b ? { title: b.title, subtitle: b.subtitle, image: b.image || hero.image, cta_text: b.cta_text, cta_link: b.cta_link, eyebrow: hero.eyebrow } : hero;
  return (
    <section className="relative grain overflow-hidden" data-testid="hero-section">
      <div className="container-x grid lg:grid-cols-12 gap-10 items-center py-12 sm:py-20">
        <div className="lg:col-span-6 space-y-6 fade-up">
          <p className="eyebrow">{h.eyebrow}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-[#1B3B2B]" data-testid="hero-title">{h.title}</h1>
          <p className="text-base sm:text-lg text-stone-600 max-w-lg leading-relaxed">{h.subtitle}</p>
          <div className="flex flex-wrap gap-3 pt-2">
            {h.cta_text && <Link to={h.cta_link || "/shop"} data-testid="hero-cta-button" className="inline-flex items-center gap-2 bg-[#1B3B2B] text-[#FBF8F3] rounded-full px-7 py-3.5 text-sm hover:bg-[#2C523A] transition-colors">{h.cta_text} <ArrowRight className="w-4 h-4" /></Link>}
            <Link to="/page/about-us" className="inline-flex items-center rounded-full px-7 py-3.5 text-sm border border-[#1B3B2B]/30 text-[#1B3B2B] hover:bg-[#E2EBE2] transition-colors">Our story</Link>
          </div>
        </div>
        <div className="lg:col-span-6 relative">
          <div className="aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] rounded-[2rem] overflow-hidden bg-[#F4EFE6]">
            <img src={img(h.image)} alt="SEED COSMETICS" className="w-full h-full object-cover" fetchpriority="high" />
          </div>
          {hero.image_note && <span className="absolute top-4 right-4 text-[10px] uppercase tracking-widest bg-white/85 px-2 py-1 rounded-full">{hero.image_note}</span>}
          <div className="absolute -bottom-5 left-4 sm:-left-6 bg-[#FFFDF9]/90 backdrop-blur-md border border-white/60 rounded-2xl px-5 py-4 shadow-sm flex items-center gap-3">
            <Leaf className="w-5 h-5 text-[#6B8E78]" />
            <div><p className="text-sm font-medium text-[#1B3B2B]">Natural · Gentle</p><p className="text-xs text-stone-500">Effective & problem-solving</p></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Newsletter({ cfg }) {
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email, consent: true });
      toast.success("Thank you for subscribing!");
      setEmail("");
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <section className="container-x mt-24" data-testid="newsletter-section">
      <div className="rounded-[2rem] bg-[#E2EBE2] p-8 sm:p-14 grid md:grid-cols-2 gap-8 items-center">
        <div><h2 className="text-3xl sm:text-4xl text-[#1B3B2B]">{cfg?.title}</h2><p className="text-stone-600 mt-3">{cfg?.text}</p></div>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" data-testid="newsletter-email-input" className="flex-1 rounded-full px-5 py-3.5 bg-[#FFFDF9] border border-[#cfdccf] outline-none text-sm" />
          <button className="rounded-full bg-[#1B3B2B] text-[#FBF8F3] px-7 py-3.5 text-sm hover:bg-[#2C523A]" data-testid="newsletter-submit-button">Subscribe</button>
        </form>
      </div>
    </section>
  );
}

export default function Home() {
  const { settings, categories } = useShop();
  const [data, setData] = useState({});
  const [banners, setBanners] = useState([]);
  const [reviews, setReviews] = useState([]);
  useSEO({ title: settings.seo?.title?.replace(" | SEED COSMETICS", ""), description: settings.seo?.description, image: settings.seo?.og_image });
  useEffect(() => {
    const get = (params) => api.get("/products", { params: { limit: 8, ...params } }).then((r) => r.data.items);
    Promise.all([get({ tag: "bestseller" }), get({ tag: "new_arrival" }), get({ tag: "featured" }), get({ offers: true, sort: "price_asc" })])
      .then(([best, fresh, featured, offers]) => setData({ best, fresh, featured, offers }));
    api.get("/banners").then((r) => setBanners(r.data));
    api.get("/reviews/featured").then((r) => setReviews(r.data));
  }, []);
  const sec = settings.sections || {};
  if (!settings.hero) return <div className="min-h-[60vh]" />;
  const empty = !data.best?.length && !data.fresh?.length && !data.featured?.length;
  return (
    <div>
      <Hero hero={settings.hero} banners={banners} />
      {categories.length > 0 && (
        <section className="container-x mt-20" data-testid="shop-by-category">
          <p className="eyebrow mb-2">Explore</p>
          <h2 className="text-3xl sm:text-4xl text-[#1B3B2B] mb-8">Shop by category</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {categories.map((c) => (
              <Link key={c.id} to={`/shop/${c.slug}`} data-testid={`category-card-${c.slug}`} className="group relative rounded-2xl overflow-hidden aspect-[4/5] bg-[#F4EFE6]">
                <img src={img(c.image)} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1B3B2B]/70 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-[#FBF8F3]"><p className="font-serif text-2xl">{c.name}</p><p className="text-xs opacity-80">{c.product_count} products</p></div>
              </Link>
            ))}
          </div>
        </section>
      )}
      {empty && <div className="container-x mt-16"><p className="text-sm text-stone-500 bg-[#F4EFE6] rounded-xl p-5" data-testid="no-products-note">Products will appear here once they are published from the Admin Panel.</p></div>}
      {sec.best_sellers !== false && <Section eyebrow="Loved most" title="Best sellers" link="/shop?tag=bestseller" items={data.best} testid="best-sellers-section" />}
      {sec.new_arrivals !== false && <Section eyebrow="Just in" title="New arrivals" link="/shop?tag=new_arrival" items={data.fresh} testid="new-arrivals-section" />}
      {sec.why !== false && settings.why_choose?.length > 0 && (
        <section className="container-x mt-24" data-testid="why-choose-section">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4"><p className="eyebrow mb-2">Why SEED</p><h2 className="text-3xl sm:text-4xl text-[#1B3B2B]">Care that keeps it simple.</h2></div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
              {settings.why_choose.map((w, i) => (
                <div key={i} className="border border-[#E8E0D5] rounded-2xl p-6 bg-[#FFFDF9]">
                  <span className="font-serif text-3xl text-[#6B8E78]">0{i + 1}</span>
                  <p className="font-semibold text-stone-900 mt-3">{w.title}</p><p className="text-sm text-stone-600 mt-1">{w.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      {sec.featured !== false && <Section eyebrow="Handpicked" title="Featured products" items={data.featured} testid="featured-section" />}
      {sec.offers !== false && <Section eyebrow="Special offers" title="On offer now" link="/shop?offers=1" items={data.offers?.slice(0, 4)} testid="offers-section" />}
      {sec.story !== false && settings.brand_story && (
        <section className="container-x mt-24" data-testid="brand-story-section">
          <div className="grid md:grid-cols-2 gap-10 items-center bg-[#F4EFE6] rounded-[2rem] overflow-hidden">
            <img src={img(settings.brand_story.image)} alt="Brand story" loading="lazy" className="w-full h-full min-h-[280px] object-cover" />
            <div className="p-8 sm:p-12 space-y-4">
              <p className="eyebrow">Brand story</p>
              <h2 className="text-3xl sm:text-4xl text-[#1B3B2B]">{settings.brand_story.title}</h2>
              <p className="text-stone-600 leading-relaxed">{settings.brand_story.text}</p>
              <Link to="/page/about-us" className="inline-flex items-center gap-1 text-sm text-[#1B3B2B] underline underline-offset-4">Read more</Link>
            </div>
          </div>
        </section>
      )}
      {sec.reviews !== false && reviews.length > 0 && (
        <section className="container-x mt-24" data-testid="reviews-section">
          <p className="eyebrow mb-2">Customer reviews</p><h2 className="text-3xl sm:text-4xl text-[#1B3B2B] mb-8">In their words</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {reviews.map((r) => (
              <div key={r.id} className="border border-[#E8E0D5] rounded-2xl p-6 bg-[#FFFDF9]">
                <div className="flex gap-0.5">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-[#C69C5C] text-[#C69C5C]" />)}</div>
                <p className="mt-3 text-stone-700">{r.body}</p><p className="mt-4 text-sm font-medium">{r.name}{r.verified_purchase && <span className="text-xs text-[#6B8E78]"> · Verified buyer</span>}</p>
              </div>
            ))}
          </div>
        </section>
      )}
      {sec.newsletter !== false && <Newsletter cfg={settings.newsletter} />}
    </div>
  );
}
